import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FileDown, PlayCircle, FileText, Film, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoryIcon = ({ category }) => {
    if (category === 'Video') return <Film className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
};

export default function ResourcesPage() {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/resources`)
            .then((res) => setResources(res.data || []))
            .catch(() => setResources([]))
            .finally(() => setLoading(false));
    }, []);

    const downloadUrl = (id) => `${API}/resources/${id}/download`;
    const inlineUrl = (id) => `${API}/resources/${id}/download?inline=true`;

    return (
        <div className="min-h-screen bg-slate-50" data-testid="resources-page">
            {/* Header */}
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="FendanaCor" className="w-10 h-10 rounded-lg" />
                        <div>
                            <p className="text-lg font-bold text-teal-700 leading-tight">FendanaCor</p>
                            <p className="text-[11px] text-slate-500 leading-tight">Fendana HealthCare Management Solutions</p>
                        </div>
                    </div>
                    <img src="/mkuh_logo.png" alt="Milton Keynes University Hospital NHS Foundation Trust" className="h-12 w-auto" />
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10">
                <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700 mb-6" data-testid="resources-back-link">
                    <ArrowLeft className="w-4 h-4" /> Back
                </Link>
                <div className="mb-8">
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">Developer Portal</p>
                    <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Resources</h1>
                    <p className="text-slate-500 mt-2 max-w-2xl">
                        Presentations, briefings and demo media for FendanaCor — including the MKUH eCare technical
                        integration enquiry and the narrated product demo video.
                    </p>
                </div>

                {loading ? (
                    <p className="text-slate-500">Loading resources…</p>
                ) : resources.length === 0 ? (
                    <p className="text-slate-500">No resources available yet.</p>
                ) : (
                    <div className="grid gap-6 md:grid-cols-2">
                        {resources.map((r) => (
                            <Card key={r.id} className="border-slate-200 overflow-hidden" data-testid={`resource-card-${r.id}`}>
                                <CardContent className="p-0">
                                    {r.category === 'Video' ? (
                                        <video
                                            controls
                                            preload="metadata"
                                            className="w-full bg-black aspect-video"
                                            src={inlineUrl(r.id)}
                                            data-testid={`resource-video-${r.id}`}
                                        />
                                    ) : (
                                        <div className="w-full aspect-video bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center">
                                            <div className="text-center text-white/90">
                                                <FileText className="w-12 h-12 mx-auto mb-2" />
                                                <p className="text-sm font-medium px-6">
                                                    {r.category === 'Captions'
                                                        ? 'SubRip captions (.srt) · accessibility'
                                                        : 'PowerPoint · 11 slides · embedded product demo'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 flex items-center gap-1">
                                                <CategoryIcon category={r.category} /> {r.category}
                                            </Badge>
                                        </div>
                                        <h3 className="font-semibold text-slate-900">{r.title}</h3>
                                        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{r.description}</p>
                                        <div className="flex items-center gap-2 mt-4">
                                            <a href={downloadUrl(r.id)} data-testid={`resource-download-${r.id}`}>
                                                <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                                                    <FileDown className="w-4 h-4 mr-2" /> Download
                                                </Button>
                                            </a>
                                            {r.category === 'Video' && (
                                                <a href={inlineUrl(r.id)} target="_blank" rel="noreferrer" data-testid={`resource-play-${r.id}`}>
                                                    <Button variant="outline">
                                                        <PlayCircle className="w-4 h-4 mr-2" /> Open in new tab
                                                    </Button>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                <footer className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-400">
                    Alphaconnections Medical Ltd · Fendana HealthCare Management Solutions · in collaboration with
                    Milton Keynes University Hospital NHS Foundation Trust
                </footer>
            </main>
        </div>
    );
}
